// packages
import React, { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'

// material ui
import Box from '@mui/system/Box'
import useMediaQuery from '@mui/material/useMediaQuery'
import Icon from '@mui/material/Icon'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'

// adx components
import StatusBar from 'components/reusable/statusBar/AdxStatusBar'
import AdxPopper from 'components/reusable/AdxPopper'

// module function & components
import { ExpenseList } from '../components/singleReport/ExpenseList'
import { ReportIconConstructor } from '../functions'

// API function
import { getReport, deleteReport } from 'services/api/modules/claim/report'
import EditReportModal from '../components/EditReportModal'
import reportStatusConverter from 'module/claim/services/helper/reportStatusConverter'
import Loading from 'components/dumb/loading/Loading'
import ClaimProgressStepper from 'module/claim/components/ClaimProgressStepper'
import ExpenseProvider from '../../expense/context/ExpenseContext'

import NewExpenseModal from '../../expense/view/NewExpenseModal'
import SubmitReportBar from '../components/singleReport/SubmitReportBar'
import WarningDeleteModal from 'module/claim/components/prompt-modal/WarningDeleteModal'
import capitalizeFirstWord from 'services/helper/capitalizeFirstWord'
import { renderCountBadge } from 'components/custom/AdxLabelRenderer'

const SingleReport = () => {
  const isLarge = useMediaQuery((theme) => theme.breakpoints.up('lg'))
  const navigate = useNavigate()
  const { pathname, state } = useLocation()
  const reportID = pathname.split('/').pop()
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)
  const [isEditReportOpen, setEditReportOpen] = useState(false)
  const [fromActionButton] = useState(state?.fromActionButton && true)

  const previousLocation = state ? state.currentLocation : null

  window.history.replaceState({}, document.title)

  // API call after determine the url param
  const {
    isLoading,
    // error,
    data: reportData,
    isError,
  } = useQuery(['singleReportData', reportID], async () => {
    const { data } = await getReport({ id: reportID })
    return data.report
  })

  // close page function
  const queryClient = useQueryClient()
  const handleClose = async () => {
    // await queryClient.removeQueries('singleReportData', { exact: true })
    await queryClient.invalidateQueries('singleReportData')
    await queryClient.invalidateQueries('hasAllocation')
    await queryClient.invalidateQueries('DashboardRequest')
    navigate(previousLocation || '/app/claim')
  }

  // check report status data if draft or not
  const isDraft = reportData?.report_status?.name === 'draft'
  const isRejected = reportData?.report_status?.name === 'rejected'
  const isReimbursed = reportData?.report_status?.name === 'reimbursement complete'

  useEffect(() => {
    if (isError) {
      navigate('/app/claim')
    }
  }, [isError])

  return (
    <ExpenseProvider>
      <Box sx={{ width: '100vw', height: '100vh' }}>
        {isLoading ? (
          <Loading show />
        ) : (
          <Box
            width={1}
            height="100vh"
            boxSizing="border-box"
            sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}
          >
            <Box
              width={{ xs: 1, lg: 'calc(100% - 450px)' }}
              bgcolor="gray-background.main"
              boxSizing="border-box"
            >
              <Box
                sx={{
                  borderBottom: isLarge && 1,
                  borderColor: 'gray-line.main',
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  boxSizing="border-box"
                  paddingX={{ xs: 2, lg: 4 }}
                  paddingY={{ xs: 2, lg: 2 }}
                  paddingTop={3}
                >
                  <Box
                    display={'flex'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    columnGap={3}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        display={{ xs: 'none', lg: 'flex' }}
                        justifyContent={'center'}
                        alignItems={'center'}
                        width={50}
                        height={50}
                        bgcolor="alternate.light"
                        borderRadius={'50%'}
                      >
                        <Icon sx={{ fontSize: 25, color: 'alternate.main' }}>
                          {ReportIconConstructor(reportData?.claim_report_type)}
                        </Icon>
                      </Box>
                      <Box>
                        <Typography
                          minWidth={{ xs: 1, lg: '400px' }}
                          variant="h4"
                          color="dark-text.main "
                          fontWeight="bold"
                          textTransform="initial"
                        >
                          {capitalizeFirstWord(reportData?.title)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Typography
                            component="span"
                            variant="body1"
                            color="alternate.main"
                            fontWeight="bold"
                          >
                            {reportData?.claim_report_type}
                          </Typography>
                          <Icon sx={{ fontSize: 7, color: 'light-gray-text.main' }}>
                            circle_rounded
                          </Icon>
                          <Typography
                            noWrap
                            component="span"
                            variant="body1"
                            color="light-gray-text.main"
                            textTransform="capitalize"
                          >
                            {reportData?.start_date} - {reportData?.end_date}
                          </Typography>
                          {reportData?.in_payroll && (
                            <Typography noWrap component="span" variant="body1" color="alternate.main">
                              {`${isReimbursed ? 'Paid via payroll' : 'Scheduled for payroll'}${
                                reportData?.payroll_period ? ` (${reportData.payroll_period})` : ''
                              }`}
                            </Typography>
                          )}
                          {isLarge && (
                            <StatusBar
                              status={reportStatusConverter(reportData?.report_status?.name)}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" columnGap={1}>
                    <Box>
                      <AdxPopper
                        linePlacement={[2]}
                        placement="bottom-end"
                        sx={{
                          width: 150,
                          boxShadow: '0px 0px 10px lightgray',
                        }}
                      >
                        {(isDraft || isRejected || isRejected) && (
                          <Typography
                            variant="body1"
                            color="dark-text.main"
                            onClick={() => setEditReportOpen(true)}
                          >
                            Edit report
                          </Typography>
                        )}

                        {!reportData?.in_payroll && (
                          <Typography
                            variant="body1"
                            color="dark-text.main"
                            onClick={() => setDeleteModalOpen(true)}
                          >
                            Delete report
                          </Typography>
                        )}
                        {/* hide as pdf function not created yet */}
                        {/* <Typography
                          variant="body1"
                          color="dark-text.main"
                          onClick={() => console.log('clicked')}
                        >
                          Export as pdf
                        </Typography> */}
                      </AdxPopper>
                      <EditReportModal
                        isEditReportOpen={isEditReportOpen}
                        setEditReportOpen={setEditReportOpen}
                        reportData={reportData}
                      />
                    </Box>
                    <CloseIcon
                      onClick={handleClose}
                      sx={{ color: 'dark-text.main', cursor: 'pointer' }}
                    />
                  </Box>
                </Box>
                {!isLarge && (
                  <StatusBar status={reportStatusConverter(reportData?.report_status?.name)} />
                )}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  boxSizing: 'border-box',
                  px: { xs: 2, lg: 4 },
                  py: { xs: 2, lg: 2 },
                }}
              >
                <Box display="flex" alignItems="center" sx={{ gap: 1 }}>
                  <Typography variant="body1" color="light-gray-text.main" fontWeight="bold">
                    Expenses
                  </Typography>
                  {renderCountBadge(reportData?.expenses ? reportData?.expenses.length : 0)}
                </Box>
                {(isDraft || isRejected) && (
                  <NewExpenseModal
                    reportData={reportData}
                    fromActionButton={fromActionButton}
                    previousSelectedCategory={state?.selectedCategory}
                  />
                )}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  px: { xs: 2, md: 4 },
                  pt: 1,
                  pb: 2,
                  overflow: 'auto',
                  maxHeight: 'calc(100vh - 250px)',
                }}
                className="scrollbar"
              >
                {reportData?.expenses &&
                  reportData?.expenses?.map((item, index) => (
                    <ExpenseList
                      key={index}
                      id={item?.id}
                      reportData={reportData}
                      category={item?.expense_category?.name}
                      categoryData={item?.expense_category}
                      date={item?.date}
                      merchant={item?.merchant}
                      distance={item?.distance}
                      description={item?.description}
                      amount={item?.amount}
                      proofLink={item?.proof_link}
                      file={{
                        name: item?.attachment,
                        url: item?.attachment_url,
                      }}
                      isDraft={isDraft || isRejected}
                    />
                  ))}
              </Box>
              <SubmitReportBar
                reportID={reportID}
                totalAmount={parseFloat(reportData?.amount_total).toFixed(2)}
                totalReimbursed={parseFloat(reportData?.amount_payable).toFixed(2)}
                handleReportClose={handleClose}
                isSubmit={isDraft || isRejected}
                isReimbursed={isReimbursed}
              />
            </Box>
            <Box
              width={{ xs: 1, lg: '450px' }}
              bgcolor="white.main"
              boxSizing="border-box"
              sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: 2, pb: 20 }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body1" color="light-gray-text.main" fontWeight={700}>
                  Report purpose
                </Typography>
                <Typography
                  variant="body1"
                  color="dark-gray.main"
                  sx={{
                    '&:first-letter': {
                      textTransform: 'uppercase',
                    },
                  }}
                >
                  {reportData?.description}
                </Typography>
              </Box>
              {reportData?.feedback && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body1" color="light-gray-text.main" fontWeight={700}>
                    Feedback
                  </Typography>
                  <Typography variant="body1" color="dark-gray.main">
                    {reportData?.feedback}
                  </Typography>
                </Box>
              )}
              <ClaimProgressStepper reportData={reportData} />
            </Box>
            <WarningDeleteModal
              id={reportData?.id}
              deleteAPI={() => deleteReport(reportData?.id)}
              reactQueryKeys={[
                'unsubmittedReportsData',
                'pendingApproverReportsData',
                'CompletedReportsData',
                'RejectedReportsData',
              ]}
              title={`Delete ${reportData?.title}`}
              subtitle="You will lose all expenses under this report."
              isOpen={isDeleteModalOpen}
              closeModalFunction={setDeleteModalOpen}
              successFunction={handleClose}
            />
          </Box>
        )}
      </Box>
    </ExpenseProvider>
  )
}

export default SingleReport
